import React, { useState, useEffect, useCallback } from "react";
import { styles } from "../../styles/theme";

// Generar un Sudoku 6x6 resuelto
function generateSolvedSudoku() {
  const grid = Array(6).fill(null).map(() => Array(6).fill(0));
  
  // Llenar el grid usando backtracking
  const isValid = (grid, row, col, num) => {
    // Verificar fila
    for (let x = 0; x < 6; x++) {
      if (grid[row][x] === num) return false;
    }
    
    // Verificar columna
    for (let x = 0; x < 6; x++) {
      if (grid[x][col] === num) return false;
    }
    
    // Verificar región 2x3
    const startRow = row - (row % 2);
    const startCol = col - (col % 3);
    for (let i = 0; i < 2; i++) {
      for (let j = 0; j < 3; j++) {
        if (grid[i + startRow][j + startCol] === num) return false;
      }
    }
    
    return true;
  };
  
  const fillGrid = (grid) => {
    for (let row = 0; row < 6; row++) {
      for (let col = 0; col < 6; col++) {
        if (grid[row][col] === 0) {
          const numbers = [1, 2, 3, 4, 5, 6].sort(() => Math.random() - 0.5);
          for (let num of numbers) {
            if (isValid(grid, row, col, num)) {
              grid[row][col] = num;
              if (fillGrid(grid)) return true;
              grid[row][col] = 0;
            }
          }
          return false;
        }
      }
    }
    return true;
  };
  
  fillGrid(grid);
  return grid;
}

// Crear puzzle removiendo números
function createPuzzle(solvedGrid, difficulty = 20) {
  const puzzle = solvedGrid.map(row => [...row]);
  let cellsToRemove = difficulty;
  
  while (cellsToRemove > 0) {
    const row = Math.floor(Math.random() * 6);
    const col = Math.floor(Math.random() * 6);
    if (puzzle[row][col] !== 0) {
      puzzle[row][col] = 0;
      cellsToRemove--;
    }
  }
  
  return puzzle;
}

function MiniSudoku({ canPlay, onWin }) {
  const [solvedGrid, setSolvedGrid] = useState([]);
  const [puzzle, setPuzzle] = useState([]);
  const [userGrid, setUserGrid] = useState([]);
  const [initialCells, setInitialCells] = useState(new Set());
  const [done, setDone] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [sudokusCompleted, setSudokusCompleted] = useState(0);
  const [hasTriggeredWin, setHasTriggeredWin] = useState(false);
  const [lives, setLives] = useState(5);
  const maxSudokusPerRound = 5;

  // Generar nuevo puzzle
  const generateNewPuzzle = useCallback(() => {
    const solved = generateSolvedSudoku();
    const newPuzzle = createPuzzle(solved, 20); // 20 celdas vacías
    
    setSolvedGrid(solved);
    setPuzzle(newPuzzle);
    setUserGrid(newPuzzle.map(row => [...row]));
    
    // Marcar celdas iniciales (no editables)
    const initial = new Set();
    newPuzzle.forEach((row, i) => {
      row.forEach((cell, j) => {
        if (cell !== 0) {
          initial.add(`${i}-${j}`);
        }
      });
    });
    setInitialCells(initial);
    setDone(false);
    setSelectedCell(null);
    setHasTriggeredWin(false);
    setLives(5);
  }, []);

  // Reiniciar al cambiar de ronda
  useEffect(() => {
    setSudokusCompleted(0);
    generateNewPuzzle();
  }, [canPlay]);

  // Verificar si está completo y correcto
  const checkSolution = useCallback(() => {
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 6; j++) {
        if (userGrid[i][j] !== solvedGrid[i][j]) {
          return false;
        }
      }
    }
    return true;
  }, [userGrid, solvedGrid]);

  // Manejar entrada de número
  const handleNumberInput = (num) => {
    if (!canPlay || done || !selectedCell) return;
    
    const [row, col] = selectedCell.split('-').map(Number);
    if (initialCells.has(`${row}-${col}`)) return; // No editar celdas iniciales
    
    const newGrid = userGrid.map(r => [...r]);
    const previousValue = newGrid[row][col];
    newGrid[row][col] = num;
    setUserGrid(newGrid);
    
    // Verificar si el número ingresado es incorrecto
    if (num !== solvedGrid[row][col]) {
      // Solo restar vida si estamos cambiando de un valor correcto/vacío a uno incorrecto
      // O si el valor previo era 0 (celda vacía)
      if (previousValue === 0 || previousValue === solvedGrid[row][col]) {
        const newLives = lives - 1;
        setLives(newLives);
        
        // Si se acaban las vidas, resetear el sudoku
        if (newLives <= 0) {
          setTimeout(() => {
            generateNewPuzzle();
          }, 500); // Pequeño delay para que el jugador vea el error
          return;
        }
      }
    }
    
    // Verificar si está completo usando newGrid directamente
    const allFilled = newGrid.every(row => row.every(cell => cell !== 0));
    if (allFilled) {
      // Verificar solución con newGrid
      const isCorrect = newGrid.every((row, i) => 
        row.every((cell, j) => cell === solvedGrid[i][j])
      );
      
      if (isCorrect) {
        setDone(true);
        if (!hasTriggeredWin && sudokusCompleted < maxSudokusPerRound) {
          setHasTriggeredWin(true);
          setSudokusCompleted(prev => prev + 1);
          onWin();
        }
      }
    }
  };

  // Manejar siguiente Sudoku
  const handleNextSudoku = () => {
    if (sudokusCompleted >= maxSudokusPerRound) return;
    generateNewPuzzle();
  };

  // Limpiar celda
  const handleClear = () => {
    if (!selectedCell || done) return;
    const [row, col] = selectedCell.split('-').map(Number);
    if (initialCells.has(`${row}-${col}`)) return;
    
    const newGrid = userGrid.map(r => [...r]);
    newGrid[row][col] = 0;
    setUserGrid(newGrid);
  };

  return (
    <div style={miniStyles.container}>
      <div style={miniStyles.header}>
        <span style={miniStyles.title}>
          Mini Sudoku 6×6 ({sudokusCompleted}/{maxSudokusPerRound})
        </span>
        <div style={miniStyles.livesContainer}>
          <span style={miniStyles.livesText}>Vidas: </span>
          {[...Array(5)].map((_, i) => (
            <span key={i} style={{
              ...miniStyles.heart,
              opacity: i < lives ? 1 : 0.3
            }}>
              ❤️
            </span>
          ))}
        </div>
        <button 
          style={styles.smallButton} 
          onClick={generateNewPuzzle}
          disabled={!canPlay || done}
        >
          Nuevo
        </button>
      </div>
      
      <div style={miniStyles.text}>
        Completa el Sudoku. Cada fila, columna y región 2×3 debe tener números del 1 al 6.
      </div>

      <div style={miniStyles.gridContainer}>
        {userGrid.map((row, i) => (
          <div key={i} style={miniStyles.row}>
            {row.map((cell, j) => {
              const isInitial = initialCells.has(`${i}-${j}`);
              const isSelected = selectedCell === `${i}-${j}`;
              const isWrong = cell !== 0 && cell !== solvedGrid[i][j];
              
              return (
                <button
                  key={`${i}-${j}`}
                  style={{
                    ...miniStyles.cell,
                    ...(isInitial && miniStyles.initialCell),
                    ...(isSelected && miniStyles.selectedCell),
                    ...(isWrong && miniStyles.wrongCell),
                  }}
                  onClick={() => !done && setSelectedCell(`${i}-${j}`)}
                  disabled={!canPlay || done}
                >
                  {cell !== 0 ? cell : ''}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {selectedCell && !done && canPlay && (
        <div style={miniStyles.controls}>
          <div style={miniStyles.numberPad}>
            {[1, 2, 3, 4, 5, 6].map(num => (
              <button
                key={num}
                style={miniStyles.numberButton}
                onClick={() => handleNumberInput(num)}
              >
                {num}
              </button>
            ))}
          </div>
          <button style={miniStyles.clearButton} onClick={handleClear}>
            Borrar
          </button>
        </div>
      )}

      {done && sudokusCompleted < maxSudokusPerRound && (
        <div style={miniStyles.nextContainer}>
          <div style={styles.miniDone}>¡Sudoku completado! 🎉</div>
          <button 
            style={miniStyles.nextButton} 
            type="button" 
            onClick={handleNextSudoku}
          >
            Siguiente Sudoku ({sudokusCompleted}/{maxSudokusPerRound})
          </button>
        </div>
      )}

      {done && sudokusCompleted >= maxSudokusPerRound && (
        <div style={styles.miniDone}>
          ¡Completado! {sudokusCompleted}/{maxSudokusPerRound} sudokus 🎉
        </div>
      )}

      {!canPlay && <div style={miniStyles.text}>La ronda aún no está activa.</div>}
    </div>
  );
}

const miniStyles = {
  container: {
    background: "rgba(0, 20, 40, 0.9)",
    borderRadius: 8,
    border: "1px solid rgba(0, 155, 255, 0.35)",
    padding: 8,
    fontSize: 12,
    color: "#E8F6FF",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
    gap: 8,
  },
  title: {
    fontWeight: 600,
    fontSize: 12,
  },
  livesContainer: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  livesText: {
    fontSize: 11,
    fontWeight: 600,
    color: "#A8D5FF",
  },
  heart: {
    fontSize: 14,
    transition: "opacity 0.3s ease",
  },
  text: {
    fontSize: 11,
    marginBottom: 6,
    color: "#A8D5FF",
  },
  gridContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    margin: "8px auto",
    width: "fit-content",
  },
  row: {
    display: "flex",
    gap: 1,
  },
  cell: {
    width: 32,
    height: 32,
    border: "1px solid rgba(0, 155, 255, 0.45)",
    background: "rgba(0, 40, 80, 0.8)",
    color: "#E8F6FF",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    borderRadius: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  initialCell: {
    background: "rgba(0, 80, 160, 0.6)",
    color: "#5DB6FF",
    cursor: "default",
  },
  selectedCell: {
    border: "2px solid #0099FF",
    background: "rgba(0, 155, 255, 0.3)",
  },
  wrongCell: {
    background: "rgba(255, 50, 50, 0.2)",
    color: "#ff8888",
  },
  controls: {
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  numberPad: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: 4,
  },
  numberButton: {
    padding: "8px 4px",
    border: "1px solid rgba(0, 155, 255, 0.45)",
    background: "linear-gradient(135deg, #0077CC, #0099FF)",
    color: "#001A33",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    borderRadius: 4,
  },
  clearButton: {
    padding: "6px",
    border: "1px solid #ff7f7f55",
    background: "rgba(255, 80, 80, 0.3)",
    color: "#ffcccc",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    borderRadius: 4,
  },
  nextContainer: {
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    alignItems: "center",
  },
  nextButton: {
    borderRadius: 999,
    border: "none",
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    background: "linear-gradient(135deg, #0077CC, #0099FF)",
    color: "#001A33",
    width: "100%",
  },
};

export default MiniSudoku;
